<script lang="ts">
  // Scoreboard — placar do topo. 3 colunas:
  //  RODADAS (vazas decididas desta mao com win/loss/tie),
  //  TENTOS (valor atual da mao — 1/3/6/9/12),
  //  PONTOS (placar geral do match — meu time vs oponente).
  import type { Card as CardType, MatchSnapshot, PlayerSnapshot } from '../../stores/match';
  import { cardStrength } from '../../lib/cards';

  type Props = {
    snap: MatchSnapshot;
    me: PlayerSnapshot;
    opp: PlayerSnapshot;
    vira: CardType | null | undefined;
  };
  let { snap, me, opp, vira }: Props = $props();

  // Resultado de cada vaza fechada do meu ponto de vista:
  // 'win' | 'lose' | 'tie' | null (vaza ainda em aberto)
  type TrickResult = 'win' | 'lose' | 'tie' | null;

  let trickDots = $derived.by((): TrickResult[] => {
    const mePlays = [me.first_play, me.second_play, me.third_play];
    const oppPlays = [opp.first_play, opp.second_play, opp.third_play];
    const results: TrickResult[] = [null, null, null];
    for (let i = 0; i < 3; i++) {
      const m = mePlays[i];
      const o = oppPlays[i];
      if (!m?.value || !o?.value) continue;
      const ms = cardStrength(m, vira);
      const os = cardStrength(o, vira);
      if (ms > os) results[i] = 'win';
      else if (os > ms) results[i] = 'lose';
      else results[i] = 'tie';
    }
    return results;
  });

  let mePoints = $derived(me.game_score ?? 0);
  let oppPoints = $derived(opp.game_score ?? 0);
  let tentos = $derived(snap.round_points || 1);
</script>

<div class="scoreboard">
  <div class="box">
    <div class="label">RODADAS</div>
    <div class="dots">
      {#each trickDots as r, i (i)}
        <span class="dot" data-result={r ?? 'pending'}></span>
      {/each}
    </div>
  </div>

  <div class="box tentos">
    <div class="label">TENTOS</div>
    <div class="tentos-val">{tentos}</div>
  </div>

  <div class="box">
    <div class="label">PONTOS</div>
    <div class="pontos">
      <span class="cell me">{mePoints}</span>
      <span class="cell opp">{oppPoints}</span>
    </div>
  </div>
</div>

<style>
  .scoreboard {
    display: inline-flex;
    align-items: stretch;
    gap: 6px;
    padding: 6px;
    background: rgba(0, 0, 0, 0.35);
    border: 1px solid var(--border);
    border-radius: var(--radius-md);
  }
  .box {
    display: flex;
    flex-direction: column;
    align-items: center;
    gap: 4px;
    padding: 6px 12px;
    min-width: 64px;
  }
  .label {
    font-size: 9px;
    letter-spacing: 2px;
    color: var(--text-dim);
    text-transform: uppercase;
  }

  .dots {
    display: flex;
    gap: 5px;
    align-items: center;
    height: 26px;
  }
  .dot {
    width: 11px;
    height: 11px;
    border-radius: 50%;
    border: 1px solid var(--border);
    background: rgba(255, 255, 255, 0.05);
  }
  .dot[data-result='win']  { background: var(--ok); border-color: var(--ok); box-shadow: 0 0 8px rgba(108, 255, 189, 0.6); }
  .dot[data-result='lose'] { background: var(--danger); border-color: var(--danger); }
  .dot[data-result='tie']  { background: var(--warn); border-color: var(--warn); }

  .tentos-val {
    font-size: 22px;
    font-weight: 800;
    line-height: 1;
    color: var(--ok);
    min-width: 26px;
    text-align: center;
  }

  .pontos {
    display: inline-flex;
    gap: 4px;
  }
  .pontos .cell {
    min-width: 28px;
    padding: 4px 8px;
    border-radius: 6px;
    font-size: 14px;
    font-weight: 700;
    text-align: center;
    line-height: 1;
  }
  .pontos .me { background: rgba(108, 255, 189, 0.18); color: var(--ok); }
  .pontos .opp { background: rgba(255, 76, 126, 0.18); color: var(--danger); }
</style>
